import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import DataTable from "../DataTable.jsx";
import { getDatum } from "../../services/formatService.js";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useObjectActions } from "../../hooks/useObjectActions.jsx";
import PropTypes from "prop-types";
import {
  AddCircleOutlineOutlined,
  FileDownloadOutlined,
  MapsHomeWorkOutlined,
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { TableEntityCell } from "../../theme/components/index.js";

export default function ObjectsTable({
  onRowClick = () => {},
  onClickNew = () => {},
  pageTitle,
  pageSubtitle,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { state: objectsState, setState: setObjectsState } = useObjectActions();

  const tableKey = "objects";
  const storageKey = "v2_table_objects";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Objekte",
        route: `/data/objekts`,
        exportRoute: `/data/objekts/export`,
        exportFilename: `objekte_export`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          include: ["vertrag"],
          sort: [{ field: "vertrag.ablauf", order: "desc" }],
        },
        onStateChange: setObjectsState,
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, exportData, tableExportStates } =
    useMultiTableData(tablesConfig, enqueueSnackbar);

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    updateTableState(tableKey, { shouldFetch: true });
  }, [tableKey]);

  const rows = objectsState?.data?.data || [];

  const handleExportExcel = () => {
    exportData(tableKey, "excel");
  };

  const toolbarActions = [
    {
      type: "button",
      label: "Neues Objekt",
      icon: <AddCircleOutlineOutlined />,
      onClick: onClickNew,
      variant: "primary",
    },
    {
      label: "Excel Export",
      type: "icon",
      variant: "tertiary",
      icon: tableExportStates[tableKey].isExporting ? (
        <CircularProgress color="primary" size={32} thickness={5} />
      ) : (
        <FileDownloadOutlined />
      ),
      onClick: handleExportExcel,
      disabled: tableExportStates[tableKey].isExporting,
    },
  ];

  const columns = [
    {
      accessorKey: "bezeichnung",
      header: "Objekt",
      minSize: 280,
      Cell: ({ row }) => {
        const o = row.original || {};
        return (
          <TableEntityCell
            title={o.bezeichnung || "—"}
            subtitle={o.typ}
            avatar={{ icon: <MapsHomeWorkOutlined />, label: o.bezeichnung }}
          />
        );
      },
    },
    {
      accessorKey: "vertrag.vertragsnummer",
      header: "Vertragsnummer",
    },
    {
      accessorKey: "vertrag.beginn",
      header: "Beginn",
      maxSize: 120,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatum(objectToRender.vertrag?.beginn)}</span>;
      },
    },
    {
      accessorKey: "vertrag.ablauf",
      header: "Ablauf",
      maxSize: 120,
      Cell: (tableCellObject, passedItem) => {
        const objectToRender = tableCellObject
          ? tableCellObject.row.original
          : passedItem;
        return <span>{getDatum(objectToRender.vertrag?.ablauf)}</span>;
      },
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        pageTitle={pageTitle}
        pageSubtitle={pageSubtitle}
        initialState={{ showGlobalFilter: true }}
        isLoading={tableState.isLoading}
        hasLoaded={tableState.hasLoaded}
        pageIndex={tableState.pageIndex}
        pageSize={tableState.pageSize}
        totalRows={tableState.totalRows}
        onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
        onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
        onChangeGlobalFilter={(searchword) => {
          updateTableState(tableKey, { searchword });
        }}
        onChangePage={(nextOrUpdater) => {
          const base = {
            pageIndex: tableState.pageIndex ?? 0,
            pageSize: tableState.pageSize ?? defaultRowsPerPage,
          };
          const next =
            typeof nextOrUpdater === "function"
              ? nextOrUpdater(base)
              : nextOrUpdater;
          if (!next) {
            return;
          }
          updateTableState(tableKey, {
            pageIndex: next.pageIndex,
            pageSize: next.pageSize,
          });
        }}
        storageKey={storageKey}
        enableHeader
        enableSorting
        enableGlobalFilter={true}
        enableColumnFilters={true}
        enableColumnHiding={true}
        topToolbarIcons={true}
        enableTopToolbar={true}
        toolbarActions={toolbarActions}
        onRowClick={onRowClick}
      />
    </>
  );
}

ObjectsTable.propTypes = {
  onRowClick: PropTypes.func,
  onClickNew: PropTypes.func,
  onClickNewCommunication: PropTypes.func,
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
};
